/**
 * Desktop: same behaviour as @refinedev/antd ThemedSider plus draggable right-edge width
 * (persisted in localStorage). Mobile: delegates to ThemedSider, fed the same menu tree.
 *
 * Based on @refinedev/antd 6.0.3 themedLayout/sider.
 */
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Layout, Menu, Grid, Button, theme, ConfigProvider } from 'antd';
import {
  LogoutOutlined,
  UnorderedListOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import {
  type TreeMenuItem,
  useTranslate,
  useLogout,
  CanAccess,
  useIsExistAuthentication,
  useMenu,
  useLink,
  useWarnAboutChange,
} from '@refinedev/core';
import {
  ThemedSider,
  ThemedTitle,
  useThemedLayoutContext,
} from '@refinedev/antd';
import { refineResourceLeaf } from '@/utils/refineResourceLabel';
import {
  hasParentListItem,
  parentListKey,
  resolveSelectedKey,
} from './siderMenuKeys';

type ThemedSiderProps = React.ComponentProps<typeof ThemedSider>;

const SIDER_WIDTH_KEY = 'novelist-admin-sider-width';
const DEFAULT_SIDER_WIDTH = 220;
const MIN_SIDER_WIDTH = 200;
const MAX_SIDER_WIDTH = 520;

function readStoredSiderWidth(): number {
  try {
    const raw = localStorage.getItem(SIDER_WIDTH_KEY);
    const n = raw ? Number.parseInt(raw, 10) : DEFAULT_SIDER_WIDTH;
    if (!Number.isFinite(n)) return DEFAULT_SIDER_WIDTH;
    return Math.min(MAX_SIDER_WIDTH, Math.max(MIN_SIDER_WIDTH, n));
  } catch {
    return DEFAULT_SIDER_WIDTH;
  }
}

/**
 * Menu tree shared by the desktop and mobile siders. Differs from the stock
 * one in a single case: a resource with its own list page *and* children
 * (e.g. publications → premieres) would render as a `SubMenu` with a plain
 * text title and lose its link, so it gets a first child pointing at its list.
 */
const useSiderMenuTree = ({
  meta,
  activeItemDisabled,
  siderCollapsed,
}: {
  meta: ThemedSiderProps['meta'];
  activeItemDisabled: boolean;
  siderCollapsed: boolean;
}) => {
  const Link = useLink();
  const translate = useTranslate();
  const { menuItems, selectedKey, defaultOpenKeys } = useMenu({ meta });
  const resolvedSelectedKey = resolveSelectedKey(menuItems, selectedKey);

  const renderTreeView = (tree: TreeMenuItem[], selKey?: string) => {
    return tree.map((item: TreeMenuItem) => {
      const { key, name, children, meta: itemMeta, list } = item;
      const parentName = itemMeta?.parent;
      const label = item?.label ?? itemMeta?.label ?? name;
      const icon = itemMeta?.icon;
      const route = list;

      const linkStyle = (isSelected: boolean): React.CSSProperties =>
        activeItemDisabled && isSelected ? { pointerEvents: 'none' } : {};

      if (children.length > 0) {
        const listKey = parentListKey(item.key ?? name);
        const isListSelected = listKey === selKey;

        return (
          <CanAccess
            key={item.key}
            resource={name}
            action="list"
            params={{
              resource: item,
            }}
          >
            <Menu.SubMenu
              key={item.key}
              icon={icon ?? <UnorderedListOutlined />}
              title={label}
            >
              {hasParentListItem(item) ? (
                <Menu.Item key={listKey} style={linkStyle(isListSelected)}>
                  <Link
                    to={typeof route === 'string' ? route : ''}
                    style={linkStyle(isListSelected)}
                  >
                    {translate(
                      `resources.${refineResourceLeaf(name)}_all`,
                      label
                    )}
                  </Link>
                  {!siderCollapsed && isListSelected && (
                    <div className="ant-menu-tree-arrow" />
                  )}
                </Menu.Item>
              ) : null}
              {renderTreeView(children, selKey)}
            </Menu.SubMenu>
          </CanAccess>
        );
      }
      const isSelected = key === selKey;
      const isRoute = !(parentName !== undefined && children.length === 0);

      return (
        <CanAccess
          key={item.key}
          resource={name}
          action="list"
          params={{
            resource: item,
          }}
        >
          <Menu.Item
            key={item.key}
            icon={icon ?? (isRoute && <UnorderedListOutlined />)}
            style={linkStyle(isSelected)}
          >
            <Link to={route ?? ''} style={linkStyle(isSelected)}>
              {label}
            </Link>
            {!siderCollapsed && isSelected && (
              <div className="ant-menu-tree-arrow" />
            )}
          </Menu.Item>
        </CanAccess>
      );
    });
  };

  return {
    items: renderTreeView(menuItems, resolvedSelectedKey),
    menuItems,
    selectedKey: resolvedSelectedKey,
    defaultOpenKeys,
  };
};

export const ResizableThemedSider: React.FC<ThemedSiderProps> = (props) => {
  const breakpoint = Grid.useBreakpoint();
  const isMobile =
    typeof breakpoint.lg === 'undefined' ? false : !breakpoint.lg;

  if (isMobile) {
    return <ThemedSiderMobile {...props} />;
  }

  return <ResizableThemedSiderDesktop {...props} />;
};

/**
 * Stock mobile drawer, fed our menu tree through `render` so a parent-with-list
 * keeps its link there too. The drawer's own `Menu` still selects by the raw
 * `useMenu` key, so the synthetic list child is reachable but not highlighted.
 */
const ThemedSiderMobile: React.FC<ThemedSiderProps> = (props) => {
  const { siderCollapsed } = useThemedLayoutContext();
  const { items } = useSiderMenuTree({
    meta: props.meta,
    activeItemDisabled: props.activeItemDisabled ?? false,
    siderCollapsed,
  });

  return (
    <ThemedSider
      {...props}
      render={
        props.render ?? (({ logout }) => [...items, logout].filter(Boolean))
      }
    />
  );
};

const ResizableThemedSiderDesktop: React.FC<ThemedSiderProps> = ({
  Title: TitleFromProps,
  render,
  meta,
  fixed,
  activeItemDisabled = false,
  siderItemsAreCollapsed = true,
}) => {
  const { token } = theme.useToken();
  const { siderCollapsed, setSiderCollapsed } = useThemedLayoutContext();

  const direction = useContext(ConfigProvider.ConfigContext)?.direction;
  const rtl = direction === 'rtl';

  const [siderWidth, setSiderWidth] = useState(readStoredSiderWidth);
  const [resizing, setResizing] = useState(false);
  const dragRef = useRef<{ startX: number; startW: number } | null>(null);
  const latestWidthRef = useRef(siderWidth);
  useEffect(() => {
    latestWidthRef.current = siderWidth;
  }, [siderWidth]);

  const isExistAuthentication = useIsExistAuthentication();
  const { warnWhen, setWarnWhen } = useWarnAboutChange();
  const translate = useTranslate();
  const { items, menuItems, selectedKey, defaultOpenKeys } = useSiderMenuTree({
    meta,
    activeItemDisabled,
    siderCollapsed,
  });
  const { mutate: mutateLogout } = useLogout();

  const RenderToTitle = TitleFromProps ?? ThemedTitle;

  const handleLogout = () => {
    if (warnWhen) {
      const confirm = window.confirm(
        translate(
          'warnWhenUnsavedChanges',
          'Are you sure you want to leave? You have unsaved changes.'
        )
      );

      if (confirm) {
        setWarnWhen(false);
        mutateLogout();
      }
    } else {
      mutateLogout();
    }
  };

  const logout = isExistAuthentication && (
    <Menu.Item
      key="logout"
      onClick={() => handleLogout()}
      icon={<LogoutOutlined />}
    >
      {translate('buttons.logout', 'Logout')}
    </Menu.Item>
  );

  const defaultExpandMenuItems = (() => {
    if (siderItemsAreCollapsed) return [];
    return menuItems.map(({ key }) => key);
  })();

  const renderSider = () => {
    if (render) {
      return render({
        items,
        logout,
        collapsed: siderCollapsed,
      });
    }
    return [...items, logout].filter(Boolean);
  };

  const renderMenu = () => {
    return (
      <Menu
        selectedKeys={selectedKey ? [selectedKey] : []}
        defaultOpenKeys={[...defaultOpenKeys, ...defaultExpandMenuItems]}
        mode="inline"
        style={{
          paddingTop: '8px',
          border: 'none',
          overflow: 'auto',
          height: 'calc(100% - 72px)',
        }}
      >
        {renderSider()}
      </Menu>
    );
  };

  const onResizePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (siderCollapsed) return;
      e.preventDefault();
      setResizing(true);
      (e.target as HTMLDivElement).setPointerCapture(e.pointerId);
      dragRef.current = { startX: e.clientX, startW: latestWidthRef.current };
    },
    [siderCollapsed]
  );

  const onResizePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragRef.current) return;
      const delta = rtl
        ? dragRef.current.startX - e.clientX
        : e.clientX - dragRef.current.startX;
      const next = Math.min(
        MAX_SIDER_WIDTH,
        Math.max(MIN_SIDER_WIDTH, dragRef.current.startW + delta)
      );
      latestWidthRef.current = next;
      setSiderWidth(next);
    },
    [rtl]
  );

  const endResize = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current) {
      try {
        localStorage.setItem(SIDER_WIDTH_KEY, String(latestWidthRef.current));
      } catch {
        /* ignore */
      }
    }
    dragRef.current = null;
    setResizing(false);
    if (e.target instanceof HTMLElement) {
      try {
        e.target.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    }
  }, []);

  useEffect(() => {
    if (siderCollapsed) dragRef.current = null;
  }, [siderCollapsed]);

  const siderStyles: React.CSSProperties = {
    backgroundColor: token.colorBgContainer,
    borderRight: `1px solid ${token.colorBgElevated}`,
    position: 'relative',
  };

  if (fixed) {
    siderStyles.position = 'fixed';
    siderStyles.top = 0;
    siderStyles.height = '100vh';
    siderStyles.zIndex = 999;
  }

  const renderClosingIcons = () => {
    const iconProps = { style: { color: token.colorPrimary } };
    const OpenIcon = rtl ? RightOutlined : LeftOutlined;
    const CollapsedIcon = rtl ? LeftOutlined : RightOutlined;
    const IconComponent = siderCollapsed ? CollapsedIcon : OpenIcon;

    return <IconComponent {...iconProps} />;
  };

  const expandedWidth = siderCollapsed ? 80 : siderWidth;

  return (
    <>
      {fixed && (
        <div
          style={{
            width: expandedWidth,
            transition: resizing ? 'none' : 'all 0.2s',
          }}
        />
      )}
      <Layout.Sider
        style={siderStyles}
        width={siderCollapsed ? undefined : siderWidth}
        collapsible
        collapsed={siderCollapsed}
        onCollapse={(collapsed, type) => {
          if (type === 'clickTrigger') {
            setSiderCollapsed(collapsed);
          }
        }}
        collapsedWidth={80}
        breakpoint="lg"
        trigger={
          <Button
            type="text"
            style={{
              borderRadius: 0,
              height: '100%',
              width: '100%',
              backgroundColor: token.colorBgElevated,
            }}
          >
            {renderClosingIcons()}
          </Button>
        }
      >
        {!siderCollapsed ? (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize sidebar"
            onPointerDown={onResizePointerDown}
            onPointerMove={onResizePointerMove}
            onPointerUp={endResize}
            onPointerCancel={endResize}
            style={{
              position: 'absolute',
              top: 0,
              ...(rtl ? { left: 0 } : { right: 0 }),
              width: 6,
              zIndex: 10,
              cursor: 'col-resize',
              touchAction: 'none',
              height: 'calc(100% - 48px)',
            }}
          />
        ) : null}
        <div
          style={{
            width: expandedWidth,
            padding: siderCollapsed ? '0' : '0 16px',
            display: 'flex',
            justifyContent: siderCollapsed ? 'center' : 'flex-start',
            alignItems: 'center',
            height: '64px',
            backgroundColor: token.colorBgElevated,
            fontSize: '14px',
            boxSizing: 'border-box',
          }}
        >
          <RenderToTitle collapsed={siderCollapsed} />
        </div>
        {renderMenu()}
      </Layout.Sider>
    </>
  );
};
